import {
  LINK_MODE,
  MODE_CONTROL_RADIO,
  UPLOAD_MODE,
  UPLOAD_OR_LINK_MODES,
  UploadOrLinkMode,
} from "constants/booksHandoff";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Input from "components/DesignSystem/Input/Input";
import { FormUploadDocuments } from "components/FormUploadDocuments";
import { CaretDown } from "components/icons/CaretDown";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import { SectionField } from "types/Models/Filing";
import { DocumentField } from "types/Models/task";
import { getLinkKey } from "util/booksHandoff/getLinkKey";
import { getModeKey } from "util/booksHandoff/getModeKey";
import { isUploadOrLinkMode } from "util/booksHandoff/isUploadOrLinkMode";
import Radio from "./FilingRadioGroup";

type ModeControlProps = {
  mode: UploadOrLinkMode;
  onChange: (mode: UploadOrLinkMode) => void;
};

type ModeRadioProps = ModeControlProps & { fieldKey: string };

type UploadOrLinkFieldProps = {
  field: SectionField;
  formId: string;
  groupId: string;
  error?: string;
};

// the design uses sentence case in the dropdown and title case on the radio pair
const DROPDOWN_LABELS = {
  [UPLOAD_MODE]: "Upload document",
  [LINK_MODE]: "Add link",
};
const RADIO_LABELS = {
  [UPLOAD_MODE]: "Upload Document",
  [LINK_MODE]: "Add link",
};
const NO_DOCUMENTS: DocumentField["documents"] = [];
const noop = () => {};

const ModeDropdown = ({ mode, onChange }: ModeControlProps) => (
  <Dropdown.Root>
    <Dropdown.Trigger asChild className="t-group t-select-none">
      <Button type="button" customType="secondary" size="regular">
        <span className="t-flex t-items-center t-gap-2">
          {DROPDOWN_LABELS[mode]}
          <span className="group-data-state-open:t-rotate-180 t-flex t-text-text-30">
            <CaretDown />
          </span>
        </span>
      </Button>
    </Dropdown.Trigger>
    <Dropdown.Portal>
      <Dropdown.Content sideOffset={4} align="start" side="bottom">
        {UPLOAD_OR_LINK_MODES.map((option) => (
          <Dropdown.Item
            key={option}
            textValue={DROPDOWN_LABELS[option]}
            onSelect={() => onChange(option)}
          >
            {DROPDOWN_LABELS[option]}
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Portal>
  </Dropdown.Root>
);

const ModeRadio = ({ fieldKey, mode, onChange }: ModeRadioProps) => {
  const handleValueChange = (value: string) => {
    if (isUploadOrLinkMode(value)) {
      onChange(value);
    }
  };

  return (
    <Radio.Root value={mode} onValueChange={handleValueChange}>
      <div className="t-flex t-gap-6 sm:t-flex-col sm:t-gap-2">
        {UPLOAD_OR_LINK_MODES.map((option) => (
          <Radio.Item
            key={option}
            value={option}
            id={`${fieldKey}-${option}`}
            className="!t-w-auto"
          >
            <span className="t-block t-text-body-sm">
              {RADIO_LABELS[option]}
            </span>
          </Radio.Item>
        ))}
      </div>
    </Radio.Root>
  );
};

/** A document answered by uploading files or adding a link, switched by a dropdown beside the input or a radio pair above it */
export const UploadOrLinkField = ({
  field,
  formId,
  groupId,
  error,
}: UploadOrLinkFieldProps) => {
  const { values, setFieldValue } =
    useTypedFormikContext<Record<string, unknown>>();

  const fieldKey = field.field_key;
  const storedMode = values[getModeKey(fieldKey)];
  const mode = isUploadOrLinkMode(storedMode) ? storedMode : UPLOAD_MODE;
  const isRadioControl = field.mode_control === MODE_CONTROL_RADIO;
  const documentField: DocumentField = {
    tag: field.tag ?? "",
    type: field.type,
    field_label: "",
    label: "",
    value: false,
    documents: NO_DOCUMENTS,
    field_key: fieldKey,
    description: "",
    required: field.required,
  };

  const handleModeChange = (nextMode: UploadOrLinkMode) =>
    setFieldValue(getModeKey(fieldKey), nextMode);

  let input = (
    <FormUploadDocuments
      field={documentField}
      formInstanceId={formId}
      groupId={groupId}
      suggestedDocUpload={noop}
    />
  );
  if (mode === LINK_MODE) {
    input = (
      <Input
        withForm
        name={getLinkKey(fieldKey)}
        placeholder={field.link_placeholder}
        hideError
      />
    );
  }

  let control = (
    <div className="t-flex t-gap-2 t-items-start sm:t-flex-col">
      <ModeDropdown mode={mode} onChange={handleModeChange} />
      <div className="t-flex-1 t-min-w-0 sm:t-w-full">{input}</div>
    </div>
  );
  if (isRadioControl) {
    control = (
      <>
        <ModeRadio
          fieldKey={fieldKey}
          mode={mode}
          onChange={handleModeChange}
        />
        {input}
      </>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-2">
      {control}
      {error && (
        <div className="t-text-body-sm t-text-red t-italic t-font-light">
          {error}
        </div>
      )}
    </div>
  );
};
